import { CLEAR_EDITOR_COMMAND, COMMAND_PRIORITY_CRITICAL, TextNode } from 'lexical';
import { $createFlagNode, CautionFlagNode, VehicleFlagNode, FlagNodeType } from './flag-nodes';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalTextEntity } from '@lexical/react/useLexicalTextEntity';
import { useCallback, useEffect } from 'react';
import xor from 'lodash/xor';

const CAUTION_REGEX = /![^\s]*!/;
const VEHICLE_REGEX = /\^[^\s]*\^/;
const VEHICLE_REGEX_GLOBAL = /\^[^\s]*\^/g;
const VALID_VEHICLE_REGEX = /(^((A[KLRZ]|C[AOT]|D[CE]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[AT]|W[AIVY])-)?[\w]{1,17}$)/i;

interface FlagPluginProps {
    onReplaceVehicles?: (vehicles: string[]) => void;
}

export const getValidVehicles = (textContent: string): string[] => {
    const vehiclesFlags = textContent.match(VEHICLE_REGEX_GLOBAL);
    if (vehiclesFlags === null) {
        return [];
    }
    return vehiclesFlags.reduce((output: string[], matchedVehicle: string) => {
        const validMatchedVehicle = matchedVehicle.replace(/\^/g, '').match(VALID_VEHICLE_REGEX);
        if (validMatchedVehicle !== null && !output.includes(validMatchedVehicle[0])) {
            output.push(validMatchedVehicle[0]);
        }
        return output;
    }, []);
};

export function FlagPlugin({ onReplaceVehicles }: FlagPluginProps): JSX.Element | null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!editor.hasNodes([CautionFlagNode, VehicleFlagNode])) {
            throw new Error('FlagPlugin: required nodes not registered on editor');
        }

        let vehiclesSet = new Set<string>();

        const removeListeners = [
            // This listener is used to empty vehiclesSet when an info is created and the editor is cleared, otherwise
            // the vehicles get removed from the event when the editor clears.
            editor.registerCommand(
                CLEAR_EDITOR_COMMAND,
                () => {
                    vehiclesSet.clear();
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL
            ),

            // This listener is use to notify the panel when vehicles are added/removed
            editor.registerTextContentListener((textContent) => {
                const vehicles = Array.from(vehiclesSet);
                const updatedVehicles = getValidVehicles(textContent);
                // To figure out if there were any vehicles added or removed by this text change we
                // compare the updated vehicles set with the previous set using symmetric difference.
                // This ensures we only dispatch the replace vehicles action when there is a vehicles
                // change and not on every keypress.
                //
                // See lodash symmetric difference (xor): https://lodash.com/docs/4.17.15#xor
                const vehiclesChange = xor(updatedVehicles, vehicles);

                if (vehiclesChange.length !== 0) {
                    vehiclesSet = new Set(updatedVehicles);
                    onReplaceVehicles?.(updatedVehicles);
                }
            }),
        ];

        return () => {
            removeListeners.forEach((removeListener) => removeListener());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    const createCautionFlagNode = useCallback((textNode: TextNode): CautionFlagNode => {
        return $createFlagNode(textNode.getTextContent(), FlagNodeType.Caution);
    }, []);

    const createVehicleFlagNode = useCallback((textNode: TextNode): VehicleFlagNode => {
        return $createFlagNode(textNode.getTextContent(), FlagNodeType.Vehicle);
    }, []);

    const getFlagOffsets = useCallback((flagMatch) => {
        if (flagMatch === null) {
            return null;
        }

        const flagLength = flagMatch[0].length;
        const startOffset = flagMatch.index;
        const endOffset = startOffset + flagLength;
        return {
            end: endOffset,
            start: startOffset,
        };
    }, []);

    const getCautionFlagOffsets = useCallback((text) => {
        const cautionMatch = CAUTION_REGEX.exec(text);
        return getFlagOffsets(cautionMatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getVehicleFlagOffsets = useCallback((text) => {
        const vehicleMatch = VEHICLE_REGEX.exec(text);
        if (vehicleMatch === null) {
            return null;
        }
        const validVehicleMatch = vehicleMatch[0].replace(/\^/g, '').match(VALID_VEHICLE_REGEX);
        if (validVehicleMatch === null) {
            return null;
        }
        return getFlagOffsets(vehicleMatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLexicalTextEntity<CautionFlagNode>(
        getCautionFlagOffsets,
        CautionFlagNode,
        createCautionFlagNode
    );

    useLexicalTextEntity<VehicleFlagNode>(
        getVehicleFlagOffsets,
        VehicleFlagNode,
        createVehicleFlagNode
    );

    return null;
}
